import React from 'react';

import { Box, Grid, InputAdornment } from '@mui/material';
import _ from 'es-toolkit/compat';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { convertDollarsToCents } from '@clearverify/utils';

import { Button } from 'src/components/common';
import { Field, Form, NumberField } from 'src/components/finalForm';
import { verifierBillingValidationSchema } from 'src/constants/validation/billing';
import { useNotification } from 'src/hooks/app';
import { useVerifierBillingList } from 'src/hooks/billings/useVerifierBillingList';
import { ChangeVerifierBillingFormFields } from 'src/types/billing';

import { verifierInputFieldsVariables } from './constants';
import { ChangeVerifierBillingFormValues } from './types';
import { getChangeVerifierBillingInitialValues } from './utils';

export interface VerifierBillingRulesModalFormProps {
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
  billingRuleIds: string | string[] | undefined;
  isAllSelected?: boolean;
  voi?: number;
  voe?: number;
  reverification?: number;
}

export const VerifierBillingRulesModalForm: React.FC<VerifierBillingRulesModalFormProps> = ({
  switchClosability,
  onCloseModal,
  billingRuleIds,
  isAllSelected,
  voi,
  voe,
  reverification,
}) => {
  const { createApolloContext } = useNotification();
  const { updateVerifierBilling, bulkUpdateVerifierBilling } = useVerifierBillingList();

  const initialValues: ChangeVerifierBillingFormValues = React.useMemo(
    () => getChangeVerifierBillingInitialValues(voe, voi, reverification),
    [voe, voi, reverification],
  );

  const updateSingleVerifierBilling = React.useCallback(
    async (formValues: ChangeVerifierBillingFormValues, id: string | undefined) => {
      if (!id) return;
      await updateVerifierBilling({
        id,
        data: {
          voE: convertDollarsToCents(Number(formValues[ChangeVerifierBillingFormFields.voe])),
          voI: convertDollarsToCents(Number(formValues[ChangeVerifierBillingFormFields.voi])),
          reverification: convertDollarsToCents(
            Number(formValues[ChangeVerifierBillingFormFields.reverification]),
          ),
        },
        context: createApolloContext({
          successMessage: 'Verifier billing rule have been successfully updated',
        }),
      });
    },
    [createApolloContext, updateVerifierBilling],
  );

  const updateMultipleVerifierBilling = React.useCallback(
    async (formValues: ChangeVerifierBillingFormValues, ids: string[]) => {
      await bulkUpdateVerifierBilling({
        data: {
          voE: {
            set: convertDollarsToCents(Number(formValues[ChangeVerifierBillingFormFields.voe])),
          },
          voI: {
            set: convertDollarsToCents(Number(formValues[ChangeVerifierBillingFormFields.voi])),
          },
          reverification: {
            set: convertDollarsToCents(
              Number(formValues[ChangeVerifierBillingFormFields.reverification]),
            ),
          },
        },
        filter: !isAllSelected
          ? {
              id: {
                in: ids,
              },
            }
          : {},
        context: createApolloContext({
          successMessage: 'Verifier billing rules has been successfully updated',
        }),
      });
    },
    [bulkUpdateVerifierBilling, createApolloContext, isAllSelected],
  );

  const handleSubmit = React.useCallback(
    async (formValues: ChangeVerifierBillingFormValues) => {
      try {
        if (_.isArray(billingRuleIds)) {
          await updateMultipleVerifierBilling(formValues, billingRuleIds);
        } else {
          await updateSingleVerifierBilling(formValues, billingRuleIds);
        }

        switchClosability(true);
        onCloseModal();
      } catch {
        switchClosability(true);
      }
    },
    [
      billingRuleIds,
      onCloseModal,
      switchClosability,
      updateMultipleVerifierBilling,
      updateSingleVerifierBilling,
    ],
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateWithYupSchema(verifierBillingValidationSchema)}
    >
      {({ dirty, submitting, valid }) => (
        <React.Fragment>
          <Grid container spacing={2}>
            {verifierInputFieldsVariables.map(inputValue => (
              <Grid key={inputValue.name} size={12}>
                <Field
                  fullWidth
                  label={inputValue.label}
                  name={inputValue.name}
                  component={NumberField}
                  disabled={submitting}
                  placeholder="0"
                  inputProps={{
                    endAdornment: <InputAdornment position="end">$</InputAdornment>,
                  }}
                />
              </Grid>
            ))}
          </Grid>

          <Box display="flex" gap={2} mt={4} justifyContent="center">
            <Button variant="outlined" color="error" onClick={onCloseModal} disabled={submitting}>
              Close
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !dirty || !valid}
              loading={submitting}
            >
              Save Changes
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};
